<template>
  <div class="whitebox summary-card">
    <div class="summary-amount">
      <span class="big-value">
        {{ $n(correctedLimitValue, "currency") }}
      </span>
      <button
        class="info-button"
        :aria-label="$t('bnpl:customer_limit.calculation.title')"
        @click="calculationModal.open()"
      >
        <i class="pi pi-info-circle" />
      </button>
    </div>
    <div
      v-if="limit.expirationDate"
      class="summary-row"
    >
      <span>{{ $t("bnpl:customer_limit.summary.validity") }}</span>
      <span class="bold">{{ $d(new Date(limit.expirationDate), "longDate") }}</span>
    </div>
    <div class="summary-row">
      <span>{{ $t("bnpl:customer_limit.summary.max_deals") }}</span>
      <span class="bold">{{ $t("bnpl:pcs", { value: limit.maxDeals || 0 }) }}</span>
    </div>
    <div class="summary-row">
      <span>{{ $t("bnpl:customer_limit.summary.remaining_deals") }}</span>
      <span class="bold">{{ $t("bnpl:pcs", { value: remainingDeals }) }}</span>
    </div>
    <div
      v-if="limit.isPremiumUser"
      class="premium-band"
    >
      <span class="premium-badge">
        {{ $t("bnpl:customer_limit.summary.premium_badge") }}
      </span>
      <p>{{ $t("bnpl:customer_limit.summary.premium_description") }}</p>
    </div>
  </div>
  <CustomerLimitCalculationModal ref="calculationModal" />
</template>

<script setup>
import { useCustomerLimitStore } from '~~/stores/customerLimit';

const customerLimitStore = useCustomerLimitStore();

const calculationModal = ref(null);

const limit = computed(() => customerLimitStore.limit ?? {});

const remainingDeals = computed(() => {
  const remaining = limit.value.maxDeals - limit.value.ongoingDeals;
  return remaining > 0 ? remaining : 0;
});

const correctedLimitValue = computed(() => {
  if (!limit.value.limitValue || limit.value.limitValue <= 0) {
    return 0;
  }
  if (limit.value.limitValue > limit.value.initialLimit) {
    return limit.value.initialLimit;
  }
  return limit.value.limitValue;
});
</script>

<style scoped lang="scss">
// .whitebox carries the background, the radius and the full width the sibling
// cards have, this only adds the accent border the result card is marked with
.summary-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--brandPrimary);
  overflow: hidden;
}

.summary-amount {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
}

.big-value {
  font-size: 40px;
  font-weight: 900;
  line-height: 120%;
  color: var(--black);
  width: fit-content;
}

.info-button {
  padding: 0;
  background: none;
  border: none;
  color: var(--gray600);
  cursor: pointer;
  display: flex;

  i {
    font-size: 22px;
  }
}

.summary-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid var(--gray50);
  font-size: 14px;
}

.premium-band {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  padding: 16px 24px;
  border-top: 1px solid var(--gray50);
  background: var(--brandPrimaryLight);
  font-size: 14px;
}

.premium-badge {
  background: var(--brandPrimary);
  color: white;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  padding: 8px 16px;
  border-radius: 9999999px;
}

@media only screen and (max-width: 792px) {
  .summary-amount {
    padding: 20px 16px;
  }

  .big-value {
    font-size: 32px;
  }

  .summary-row {
    padding: 14px 16px;
  }

  .premium-band {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }
}
</style>
